// src/components/InventoryModule.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// --- Función para determinar la URL Base de la API ---
const getApiBaseUrl = () => {
  // 1. Prioriza la URL definida en la variable de entorno (para producción/staging).
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // Elimina cualquier barra diagonal sobrante al final.
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }

  // 2. Si estamos en desarrollo (localhost), usa la URL por defecto.
  if (window.location.hostname === 'localhost') {
    // Importante: ajusta el puerto 3000 si tu backend de desarrollo corre en otro.
    return 'http://localhost:3000';
  }

  // 3. En cualquier otro caso (producción sin variable de entorno),
  //    asume que la API está en el mismo origen (dominio) que el frontend.
  return window.location.origin;
};

// Obtenemos la URL base una sola vez
const API_BASE = getApiBaseUrl();
// --- Fin Función URL Base ---

function InventoryModule() {
  const { user, token } = useAuth();

  // --- Estados para la importación de inventario ---
  const [inventoryFile, setInventoryFile] = useState(null);
  const [importMessage, setImportMessage] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  // --- Estados para la lista de inventario ---
  const [inventoryItems, setInventoryItems] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // --- Estados para filtros y ordenamiento ---
  const [searchTerm, setSearchTerm] = useState('');
  const [almacenFilter, setAlmacenFilter] = useState('');
  const [sortBy, setSortBy] = useState('internal_sku'); // Columna de ordenamiento por defecto
  const [sortOrder, setSortOrder] = useState('asc'); // Orden por defecto
  const [hideUnavailable, setHideUnavailable] = useState(false); // Ocultar no disponibles

  // --- Estados para paginación ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 10 SKUs por página
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Número máximo de botones de página a mostrar (ej. 5 o 10)
  const maxPageButtons = 10; 


  // Función para obtener la lista de inventario del backend
  const fetchInventory = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      if (!user || user.rol !== 'admin' || !token) {
        setListError('Acceso denegado: Solo administradores pueden ver el inventario.');
        setListLoading(false);
        return;
      }

      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('searchTerm', searchTerm);
      if (almacenFilter) queryParams.append('almacenFilter', almacenFilter);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (hideUnavailable) queryParams.append('hideUnavailable', hideUnavailable);
      
      // Parámetros de paginación
      queryParams.append('page', currentPage);
      queryParams.append('limit', itemsPerPage);


      // *** USO DE LA URL BASE DINÁMICA ***
      const apiUrl = `${API_BASE}/api/inventory?${queryParams.toString()}`;
      console.log(`InventoryModule: Realizando fetch a ${apiUrl} con token.`);
      // **********************************
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      // Se ajusta la lógica para manejar respuestas que no son JSON
      const contentType = response.headers.get("content-type");
      let data = {};
      if (contentType && contentType.indexOf("application/json") !== -1) {
          data = await response.json();
      }

      if (response.ok) {
        setInventoryItems(data.inventory || []); // Aseguramos que sea un array
        setTotalItemsCount(data.totalItems || 0); 
        setTotalPages(data.totalPages || 1);     
        console.log('InventoryModule: Inventario cargado exitosamente:', data.inventory);
      } else {
        setListError(data.mensaje || `Error al cargar inventario: ${response.statusText}`);
        console.error('InventoryModule: Error en la respuesta del backend de inventario:', data);
      }
    } catch (err) {
      console.error('Error de red al obtener inventario:', err);
      setListError('Error de conexión con el servidor de inventario.');
    } finally {
      setListLoading(false);
    }
  }, [user, token, searchTerm, almacenFilter, sortBy, sortOrder, hideUnavailable, currentPage, itemsPerPage]); // Dependencias de fetchInventory

  // Cargar inventario cuando el componente se monta o los filtros/ordenamiento/página cambian
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);


  // --- Funciones para la importación de inventario ---
  const handleInventoryFileChange = (e) => {
    setInventoryFile(e.target.files[0]);
    setImportMessage('');
  };

  const handleImportInventory = async (e) => {
    e.preventDefault();
    setImportMessage('');
    setImportLoading(true);

    if (!inventoryFile) {
      setImportMessage('Por favor, selecciona un archivo CSV para importar.');
      setImportLoading(false);
      return;
    }

    if (!token) {
      setImportMessage('Error: No hay token de autenticación. Por favor, inicia sesión de nuevo.');
      setImportLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('inventoryFile', inventoryFile);

    try {
      // *** USO DE LA URL BASE DINÁMICA ***
      const response = await fetch(`${API_BASE}/api/inventory/import-csv`, {
      // **********************************
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setImportMessage(`Importación completada: ${data.mensaje}. Total registros CSV: ${data.total_registros_csv}, Insertados/Actualizados: ${data.insertados_actualizados}, Errores: ${data.errores}`);
        setInventoryFile(null); // Limpiar el input de archivo
        fetchInventory(); // Refrescar la lista de inventario después de la importación
      } else {
        setImportMessage(`Error en la importación: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de red al importar inventario:', err);
      setImportMessage('Error de conexión con el servidor al importar inventario.');
    } finally {
      setImportLoading(false);
    }
  };

  // --- Nueva función para exportar inventario ---
  const handleExportInventory = async () => {
    setImportMessage('Exportando inventario...');
    try {
      if (!user || user.rol !== 'admin' || !token) {
        setImportMessage('Error: Acceso denegado para exportar inventario.');
        return;
      }

      // *** USO DE LA URL BASE DINÁMICA ***
      const response = await fetch(`${API_BASE}/api/inventory/export-csv`, {
      // **********************************
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'inventario_exportado.csv'; // Nombre del archivo de descarga
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setImportMessage('Inventario exportado exitosamente.');
      } else {
        const errorData = await response.json();
        setImportMessage(`Error al exportar inventario: ${errorData.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de red al exportar inventario:', err);
      setImportMessage('Error de conexión al exportar inventario.');
    } finally {
      setTimeout(() => setImportMessage(''), 5000); // Limpiar mensaje después de 5 segundos
    }
  };

  // Manejador de cambio de página
  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Lógica para generar los botones de paginación
  const renderPaginationButtons = () => {
    const buttons = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="dots-start" className="px-3 py-1 text-gray-500">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg border border-gray-300 text-gray-700 transition duration-200 ${currentPage === i ? 'bg-blue-500 text-white hover:bg-blue-600' : 'hover:bg-gray-100'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="dots-end" className="px-3 py-1 text-gray-500">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200"
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };


  // Si el usuario no es admin, muestra un mensaje de acceso denegado
  if (!user || user.rol !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-gray-100 min-h-full">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Gestión de Inventario</h2>
        <p className="text-xl text-red-600 mb-8">Acceso Denegado: Solo administradores con sesión iniciada pueden ver esta sección.</p>
        {listError && <p className="text-red-500 text-sm">{listError}</p>}
      </div>
    );
  }


  return (
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Gestión de Inventario</h2>

      {/* Sección de Importar Inventario desde CSV */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Importar Inventario desde CSV</h3>
        <p className="text-gray-600 mb-4">
          Sube un archivo CSV con el formato: <code>SKU_Interno,Descripción,Cantidad,Almacén</code> (sin encabezados).
        </p>
        <form onSubmit={handleImportInventory} className="space-y-4">
          <div>
            <label htmlFor="inventoryFile" className="block text-gray-700 text-sm font-bold mb-2">
              Seleccionar Archivo CSV:
            </label>
            <input
              type="file"
              id="inventoryFile"
              accept=".csv"
              onChange={handleInventoryFileChange}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          {importMessage && (
            <div className={`p-3 rounded-lg text-sm font-medium ${
              importMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            }`}>
              {importMessage}
            </div>
          )}
          <button
            type="submit"
            disabled={importLoading || !inventoryFile}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importLoading ? 'Importando...' : 'Importar Inventario'}
          </button>
        </form>
      </div>

      {/* Sección de Lista de Inventario con Filtros y Ordenamiento */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Inventario Actual</h3>
          <button
            onClick={handleExportInventory}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200"
          >
            Exportar Inventario (CSV)
          </button>
        </div>

        {/* Controles de Filtro y Ordenamiento */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label htmlFor="searchTerm" className="block text-gray-700 text-sm font-bold mb-2">Buscar (SKU/Descripción)</label>
            <input
              type="text"
              id="searchTerm"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="almacenFilter" className="block text-gray-700 text-sm font-bold mb-2">Filtrar por Almacén</label>
            <input
              type="text"
              id="almacenFilter"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Almacén..."
              value={almacenFilter}
              onChange={(e) => setAlmacenFilter(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="sortBy" className="block text-gray-700 text-sm font-bold mb-2">Ordenar por</label>
            <select
              id="sortBy"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="internal_sku">SKU</option>
              <option value="internal_title">Descripción</option>
              <option value="stock_quantity">Cantidad</option>
              <option value="almacen">Almacén</option>
            </select>
          </div>
          <div>
            <label htmlFor="sortOrder" className="block text-gray-700 text-sm font-bold mb-2">Orden</label>
            <select
              id="sortOrder"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          </div>
          <div className="md:col-span-4 flex items-center">
            <input
              type="checkbox"
              id="hideUnavailable"
              className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              checked={hideUnavailable}
              onChange={(e) => setHideUnavailable(e.target.checked)}
            />
            <label htmlFor="hideUnavailable" className="text-gray-700 text-sm font-bold">Ocultar no disponibles (Cantidad = 0)</label>
          </div>
        </div>


        {listLoading ? (
          <div className="text-center text-gray-600 text-lg py-4">Cargando inventario...</div>
        ) : listError ? (
          <div className="text-center text-red-600 text-lg py-4">Error: {listError}</div>
        ) : inventoryItems.length === 0 ? (
          <p className="text-center text-gray-600">No hay ítems en el inventario que coincidan con los filtros.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DESCRIPCION</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CANTIDAD</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DISPONIBLE</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ALMACEN</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventoryItems.map((item) => (
                  <tr key={item.id}>
                    
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{item.internal_sku}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.internal_title || 'N/A'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.stock_quantity}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.stock_quantity}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.almacen || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Controles de Paginación */}
        <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow mt-4">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            {renderPaginationButtons()} {/* Llama a la función para renderizar los botones */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
          <p className="text-sm text-gray-700">
            Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalItemsCount)} de {totalItemsCount} ítems
          </p>
        </div>
      </div>
    </>
  );
}

export default InventoryModule;
